import React, { useMemo } from 'react';
import * as THREE from 'three';
import type { ViewType } from '../../utils/workspace.types';
import { CustomAxesHelper } from './CustomAxesHelper';

interface FixedGridProps {
  currentView: ViewType;
}

/**
 * StaadPro-style Grid component - renders a grid made of small squares
 * Creates a wireframe grid using individual lines forming small squares
 * Similar to graph paper appearance in StaadPro
 * 
 * Z-UP CONVENTION: Grid is on Z=0 plane (XY horizontal plane - ground level)
 * X-axis (Red): Horizontal East-West
 * Y-axis (Green): Horizontal North-South
 * Z-axis (Blue): Vertical (Elevation)
 */
export const FixedGrid: React.FC<FixedGridProps> = ({ currentView: _ }) => {
  // Grid configuration for StaadPro-style appearance
  const GRID_SIZE = 100; // Total grid size (100x100 units)
  const SQUARE_SIZE = 1; // Size of each small square (1x1 unit)
  const GRID_COLOR = '#b0b0b0'; // Lighter gray for better visibility
  const MAJOR_GRID_COLOR = '#606060'; // Darker for major lines every 10 units
  
  // Create the grid geometry using line segments
  // Z-UP: Grid on Z=0 plane (XY horizontal)
  const gridGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    
    const halfSize = GRID_SIZE / 2;
    const divisions = GRID_SIZE / SQUARE_SIZE;
    
    // Create horizontal lines (parallel to X-axis on XY plane)
    for (let i = 0; i <= divisions; i++) {
      const y = -halfSize + (i * SQUARE_SIZE);
      // Line from left to right along X-axis
      vertices.push(-halfSize, y, 0);
      vertices.push(halfSize, y, 0);
    }
    
    // Create vertical lines (parallel to Y-axis on XY plane)
    for (let i = 0; i <= divisions; i++) {
      const x = -halfSize + (i * SQUARE_SIZE);
      // Line from front to back along Y-axis
      vertices.push(x, -halfSize, 0);
      vertices.push(x, halfSize, 0);
    }
    
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    return geometry;
  }, []);
  
  // Create major grid lines every 10 units for better reference
  // Z-UP: Major grid on Z=0 plane (XY horizontal)
  const majorGridGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    
    const halfSize = GRID_SIZE / 2;
    const majorSpacing = 10; // Major lines every 10 units
    const majorDivisions = GRID_SIZE / majorSpacing;
    
    // Create major horizontal lines (parallel to X-axis on XY plane)
    for (let i = 0; i <= majorDivisions; i++) {
      const y = -halfSize + (i * majorSpacing);
      vertices.push(-halfSize, y, 0);
      vertices.push(halfSize, y, 0);
    }
    
    // Create major vertical lines (parallel to Y-axis on XY plane)
    for (let i = 0; i <= majorDivisions; i++) {
      const x = -halfSize + (i * majorSpacing);
      vertices.push(x, -halfSize, 0);
      vertices.push(x, halfSize, 0);
    }
    
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    return geometry;
  }, []);

  return (
    <group>
      {/* Major grid lines (every 10 units) - darker and more visible */}
      <lineSegments geometry={majorGridGeometry}>
        <lineBasicMaterial 
          color={MAJOR_GRID_COLOR}
          transparent={true}
          opacity={0.6}
          depthWrite={false}
        />
      </lineSegments>
      
      {/* Fine grid lines (every 1 unit) - lighter */}
      <lineSegments geometry={gridGeometry}>
        <lineBasicMaterial 
          color={GRID_COLOR}
          transparent={true}
          opacity={0.35}
          depthWrite={false}
        />
      </lineSegments>
      
      {/* Custom Bold Axes with Labels: X=Red (East), Y=Green (North), Z=Blue (Up) */}
      <CustomAxesHelper size={10} lineWidth={2} showLabels={true} />
    </group>
  );
};
